import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { cnpj as cnpjValidator } from 'cpf-cnpj-validator';
import { AlertCircle, Store, User } from 'lucide-react';

import Button from 'components/_UI/Button';
import Fieldset from 'components/_UI/Fieldset';
import fieldValidations from 'helpers/fieldValidations';
import masks from 'helpers/masks';
import colors from 'theme/colors';

import {
    Actions,
    FieldStack,
    InfoBox,
    InfoText,
    InfoTitle,
    StepShell,
    StepSubtitle,
    StepTitle,
} from '../partnerStyles';

/* `partner_type` é o que o backend recebe; o role_id existe só para telas que
   ainda leem o papel pelo id do ambiente. */
export const PARTNER_TYPES = [
    {
        value: 'Producer',
        roleId: import.meta.env.VITE_PRODUCER_ROLE_ID,
        label: 'Organizador',
        text: 'Produz eventos e vende ingressos. Não precisa de espaço físico nem de CNPJ.',
        Icon: User,
    },
    {
        value: 'Establishment',
        roleId: import.meta.env.VITE_ESTABLISHMENT_ROLE_ID,
        label: 'Estabelecimento',
        text: 'Tem um espaço físico: além de eventos, pode vender produtos no local.',
        Icon: Store,
    },
];

/**
 * Passo 1 — o que a pessoa faz.
 *
 * Esta pergunta era a última do fluxo antigo, depois de oito campos e da foto do
 * documento. Ela é a que ramifica tudo (só Estabelecimento informa CNPJ), então
 * abre o cadastro: a partir da resposta, os campos seguintes já chegam ajustados.
 */
export default function StepBusiness({ defaultValues, onSubmit }) {
    const titleRef = useRef(null);

    const {
        register,
        handleSubmit,
        watch,
        resetField,
        clearErrors,
        formState: { errors },
    } = useForm({
        mode: 'onTouched',
        defaultValues: {
            partnerType: defaultValues?.partnerType || '',
            companyName: defaultValues?.companyName || '',
            cnpj: defaultValues?.cnpj || '',
        },
    });

    const partnerType = watch('partnerType');
    const isEstablishment = partnerType === 'Establishment';

    useEffect(() => {
        titleRef.current?.focus();
    }, []);

    useEffect(() => {
        // Trocar para Organizador tira o CNPJ da tela; deixar o valor e o erro
        // para trás travaria o envio num campo que ninguém mais vê.
        if (!isEstablishment) {
            resetField('cnpj');
            clearErrors('cnpj');
        }
    }, [isEstablishment, resetField, clearErrors]);

    const companyLabel = isEstablishment
        ? 'Razão social ou nome fantasia'
        : 'Nome da produtora ou seu nome artístico';

    return (
        <StepShell>
            <StepTitle ref={titleRef} tabIndex={-1}>
                O que você faz no Whizr?
            </StepTitle>
            <StepSubtitle>
                Isso define o que aparece no seu painel e quais dados precisamos pedir.
            </StepSubtitle>

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <TypeGroup role='radiogroup' aria-label='Categoria do parceiro'>
                    {PARTNER_TYPES.map(({ value, label, text, Icon }) => (
                        <TypeCard key={value} $selected={partnerType === value}>
                            <TypeInput
                                type='radio'
                                value={value}
                                {...register('partnerType', {
                                    required: 'Escolha uma das duas opções para continuar.',
                                })}
                            />
                            <TypeIcon $selected={partnerType === value}>
                                <Icon size={22} strokeWidth={2} aria-hidden='true' />
                            </TypeIcon>
                            <TypeLabel>{label}</TypeLabel>
                            <TypeText>{text}</TypeText>
                        </TypeCard>
                    ))}
                </TypeGroup>

                {errors.partnerType && (
                    <GroupError role='alert'>
                        <AlertCircle size={14} aria-hidden='true' />
                        {errors.partnerType.message}
                    </GroupError>
                )}

                {partnerType && (
                    <FieldStack>
                        <Fieldset
                            layout='squired'
                            label={companyLabel}
                            placeholder={isEstablishment ? 'Bar do Zé 2 ME' : 'Produtora Aurora'}
                            name='companyName'
                            register={register}
                            validations={fieldValidations.companyName(companyLabel.toLowerCase())}
                            error={errors.companyName?.message}
                        />

                        {isEstablishment ? (
                            <Fieldset
                                layout='squired'
                                label='CNPJ'
                                placeholder='00.000.000/0000-00'
                                mask={masks.cnpj}
                                name='cnpj'
                                register={register}
                                validations={{
                                    required: 'O campo CNPJ é obrigatório',
                                    validate: (value) => cnpjValidator.isValid(value) || 'CNPJ inválido',
                                }}
                                error={errors.cnpj?.message}
                            />
                        ) : (
                            <InfoBox>
                                <InfoTitle>Sem CNPJ? Tudo bem</InfoTitle>
                                <InfoText>
                                    Organizador pode vender como pessoa física — os repasses caem na conta
                                    do CPF que você informar no próximo passo.
                                </InfoText>
                            </InfoBox>
                        )}
                    </FieldStack>
                )}

                <Actions>
                    <Button category='squiredBorded' variant='featured' type='submit'>
                        Continuar
                    </Button>
                </Actions>
            </form>
        </StepShell>
    );
}

const TypeGroup = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-top: 24px;
`;

const TypeCard = styled.label`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 18px 14px;
    border-radius: 12px;
    border: 1.5px solid ${({ $selected }) => ($selected ? colors.secondaryPurple : '#DDDDE2')};
    background: ${({ $selected }) => ($selected ? `${colors.secondaryPurple}0D` : colors.neutralLight)};
    cursor: pointer;
    text-align: center;
    transition: border-color 0.15s ease, background 0.15s ease;

    &:hover {
        border-color: ${colors.secondaryPurple};
    }

    /* O input é um radio de verdade (só escondido), então setas do teclado,
       leitor de tela e validação do react-hook-form funcionam sem gambiarra. */
    &:focus-within {
        outline: 2px solid ${colors.secondaryPurple};
        outline-offset: 2px;
    }
`;

const TypeInput = styled.input`
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
    margin: 0;
`;

const TypeIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${({ $selected }) => ($selected ? colors.secondaryPurple : '#EFEFF2')};
    color: ${({ $selected }) => ($selected ? '#fff' : colors.night)};
`;

const TypeLabel = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: ${colors.nightDark};
`;

const TypeText = styled.span`
    font-size: 11px;
    line-height: 1.4;
    color: ${colors.night};
`;

const GroupError = styled.p`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 10px 0 0;
    font-size: 12px;
    color: ${colors.danger};
`;
